import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { routeSettings } from 'src/app/core/routes.config';
import { DispatchDashboardAuthService } from 'src/app/features/dispatch-dashboard/services/dispatch-dashboard-auth.service';
import { RefreshToken } from 'src/app/features/users/state/users.actions';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private isTokenRefreshing: boolean = false;

  constructor(
    private store: Store,
    private router: Router,
    private dispatchDashboardAuthService: DispatchDashboardAuthService
  ) {}
    
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(this.attachTokenToRequest(request)).pipe(
      tap((_event: HttpEvent<any>) => {}),
      catchError((err): Observable<any> => {
        if (err instanceof HttpErrorResponse) {
          switch ((<HttpErrorResponse>err).status) {
            case 401:
              if (this.isDispatchDashboard401(err)) {
                return this.handleDispatchDashboard401();
              }
              console.log('Token expired. Attempting refresh ...');
              return this.handleHttpResponseError(request, next);
            default:
              return throwError(err);
          }
        } else {
          return throwError(this.handleError);
        }
      })
    );
  }

  private isDispatchDashboard401(err: HttpErrorResponse): boolean {
    const errorMessage: string = err.error?.error ?? '';
    return this.dispatchDashboardAuthService.isDispatchDashboard401Error(errorMessage);
  }

  private handleDispatchDashboard401(): Observable<never> {
    this.dispatchDashboardAuthService.clearSession();
    this.router.navigate([
      routeSettings.dispatchDashboard.path,
      routeSettings.dispatchDashboard.login.path
    ]);
    return throwError(() => new Error('Dispatch dashboard session expired.'));
  }

  handleError(errorResponse: HttpErrorResponse) {
    let errorMsg: string;
    if (errorResponse.error instanceof Error) {
      errorMsg = 'An error occured : ' + errorResponse.error.message;
    } else {
      errorMsg = `Backend returned code ${errorResponse.status}, body was: ${errorResponse.error}`;
    }
    return throwError(errorMsg);
  }

  handleHttpResponseError(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isTokenRefreshing) {
      this.isTokenRefreshing = true;
      this.tokenSubject.next(null);

      return this.store.dispatch(new RefreshToken()).pipe(
        switchMap((tokenresponse: any) => {
          if (tokenresponse) {
            return next.handle(this.attachTokenToRequest(request));
          }
        }),
        catchError(err => {
          return this.handleError(err);
        }),
        finalize(() => {
          this.isTokenRefreshing = false;
        })
      );
    } else {
      this.isTokenRefreshing = false;
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(_token => {
          return next.handle(this.attachTokenToRequest(request));
        })
      );
    }
  }

  attachTokenToRequest(request: HttpRequest<any>) {
    const token = this.store.selectSnapshot<string>((state) => state.users?.auth.access_token || null);
    const tenantId = this.store.selectSnapshot<string>((state) => state.prefillData?.tenantId || null);

    if (token) {
      return tenantId
        ? request.clone({ setHeaders: { Authorization: `Bearer ${token}`, __tenant: tenantId } })
        : request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    const dispatchSession = this.dispatchDashboardAuthService.getSession();
    if (dispatchSession?.token && this.dispatchDashboardAuthService.isSecuredEndpoint(request.url)) {
      return tenantId
        ? request.clone({ setHeaders: { Authorization: `Bearer ${dispatchSession.token}`, __tenant: tenantId } })
        : request.clone({ setHeaders: { Authorization: `Bearer ${dispatchSession.token}` } });
    }

    if (tenantId) {
      return request.clone({ setHeaders: { __tenant: tenantId } });
    }

    return request;
  }
}
